// US Federal / commonly-observed holidays used by the schedule generator.
// Returns local-date keys (YYYY-MM-DD) — never UTC — so they match the same
// keys used by DraftCalendarView for cell rendering.

const pad = (n) => String(n).padStart(2, '0');
const key = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;

// nthWeekdayOfMonth(year, month, weekday, n) — e.g. 3rd Monday in January
const nthWeekdayOfMonth = (year, month, weekday, n) => {
  const first = new Date(year, month, 1);
  const offset = (weekday - first.getDay() + 7) % 7;
  return new Date(year, month, 1 + offset + (n - 1) * 7);
};

// lastWeekdayOfMonth(year, month, weekday) — e.g. last Monday in May
const lastWeekdayOfMonth = (year, month, weekday) => {
  const last = new Date(year, month + 1, 0); // last day of month
  const offset = (last.getDay() - weekday + 7) % 7;
  return new Date(year, month, last.getDate() - offset);
};

/**
 * Build a map of holiday-key → human-readable name for a given year.
 * Includes the ten US Federal holidays plus a few commonly-observed
 * end-of-year days the institute typically does not start classes on.
 */
export const getHolidaysForYear = (year) => {
  const h = {};

  // Fixed-date holidays
  h[key(year, 0, 1)] = "New Year's Day";
  h[key(year, 6, 4)] = 'Independence Day';
  h[key(year, 10, 11)] = 'Veterans Day';
  h[key(year, 11, 24)] = 'Christmas Eve';
  h[key(year, 11, 25)] = 'Christmas Day';
  h[key(year, 11, 31)] = "New Year's Eve";

  // Floating holidays
  const memorial = lastWeekdayOfMonth(year, 4, 1);         // last Mon of May
  h[key(memorial.getFullYear(), memorial.getMonth(), memorial.getDate())] = 'Memorial Day';

  const labor = nthWeekdayOfMonth(year, 8, 1, 1);          // 1st Mon of Sep
  h[key(labor.getFullYear(), labor.getMonth(), labor.getDate())] = 'Labor Day';

  const thanksgiving = nthWeekdayOfMonth(year, 10, 4, 4);  // 4th Thu of Nov
  h[key(thanksgiving.getFullYear(), thanksgiving.getMonth(), thanksgiving.getDate())] = 'Thanksgiving Day';

  // Day after Thanksgiving (typically observed)
  const dayAfter = new Date(thanksgiving);
  dayAfter.setDate(dayAfter.getDate() + 1);
  h[key(dayAfter.getFullYear(), dayAfter.getMonth(), dayAfter.getDate())] = 'Day after Thanksgiving';

  return h;
};

/** Pre-build a multi-year holiday map covering the years a draft might span. */
export const getHolidaysForYears = (years) => {
  const merged = {};
  years.forEach(y => Object.assign(merged, getHolidaysForYear(y)));
  return merged;
};

/** Local-date key helper that the algorithm and UI both use. */
export const toLocalDateKey = (date) => {
  if (!date) return '';
  const d = (date instanceof Date) ? date : new Date(date);
  return key(d.getFullYear(), d.getMonth(), d.getDate());
};

/** Is a given Date a holiday? */
export const isHoliday = (date, holidayMap) => {
  if (!date || !holidayMap) return false;
  return Object.prototype.hasOwnProperty.call(holidayMap, toLocalDateKey(date));
};

/** Weekends and configured holidays are not instructional days. */
export const isTeachingDay = (date, holidayMap = {}) => {
  if (!date) return false;
  const day = date.getDay();
  return day !== 0 && day !== 6 && !isHoliday(date, holidayMap);
};

/** Return every instructional date in an inclusive calendar range. */
export const getTeachingDatesInRange = (startDate, endDate, holidayMap = {}) => {
  if (!startDate || !endDate || startDate > endDate) return [];
  const dates = [];
  const current = new Date(startDate);
  while (current <= endDate) {
    if (isTeachingDay(current, holidayMap)) dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
};

/** Count instructional days in an inclusive calendar range. */
export const countTeachingDays = (startDate, endDate, holidayMap = {}) =>
  getTeachingDatesInRange(startDate, endDate, holidayMap).length;

/** Move by a number of instructional days, skipping weekends and holidays. */
export const addTeachingDayOffset = (startDate, offset, holidayMap = {}) => {
  const date = new Date(startDate);
  const direction = offset < 0 ? -1 : 1;
  let remaining = Math.abs(offset);
  while (remaining > 0) {
    date.setDate(date.getDate() + direction);
    if (isTeachingDay(date, holidayMap)) remaining--;
  }
  return date;
};

/** End date for an inclusive number of instructional days. */
export const getTeachingEndDate = (startDate, durationDays, holidayMap = {}) =>
  addTeachingDayOffset(startDate, Math.max(1, durationDays || 1) - 1, holidayMap);

/**
 * Short courses must occupy one uninterrupted, holiday-free Monday-Friday
 * block. This yields Mon-Thu/Tue-Fri for four days and Monday-Friday for five.
 */
export const isValidTeachingBlock = (startDate, durationDays, holidayMap = {}) => {
  const duration = Math.max(1, durationDays || 1);
  if (!isTeachingDay(startDate, holidayMap)) return false;
  if (duration > 5) return true;
  if (startDate.getDay() + duration - 1 > 5) return false;
  for (let offset = 1; offset < duration; offset++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + offset);
    if (!isTeachingDay(date, holidayMap)) return false;
  }
  return true;
};

/** Holiday name for a Date, or '' if none. */
export const getHolidayName = (date, holidayMap) => {
  if (!date || !holidayMap) return '';
  return holidayMap[toLocalDateKey(date)] || '';
};

/**
 * Adjust a proposed start date so no class starts on a holiday.
 *  - If the start day is a holiday, shift forward to the next non-holiday weekday.
 *  - Special rule: if the original start lands on a Monday AND duration ≥ 4 days
 *    AND any day in the class span (Mon..Mon+duration-1) is a holiday, push
 *    the entire class to the *following* Monday (and re-check there too).
 */
export const adjustStartForHolidays = (proposedDate, durationDays, holidayMap, maxIterations = 8) => {
  if (!proposedDate || !holidayMap) return proposedDate;
  let date = new Date(proposedDate);
  for (let i = 0; i < maxIterations; i++) {
    const isMon = date.getDay() === 1;
    const longClass = (durationDays || 1) >= 4;
    let spansHoliday = false;
    if (isMon && longClass) {
      for (let d = 0; d < durationDays; d++) {
        const day = new Date(date);
        day.setDate(day.getDate() + d);
        if (isHoliday(day, holidayMap)) { spansHoliday = true; break; }
      }
      if (spansHoliday) {
        date.setDate(date.getDate() + 7); // push to next week (same Monday)
        continue;
      }
    }
    if (isHoliday(date, holidayMap)) {
      // Skip forward to the next non-holiday weekday
      do {
        date.setDate(date.getDate() + 1);
      } while (isHoliday(date, holidayMap) || date.getDay() === 0 || date.getDay() === 6);
      continue;
    }
    return date;
  }
  return date; // give up after maxIterations to avoid infinite loops
};
